<script lang="ts" setup>
import { computed, ref } from 'vue';
import { formatDate } from '@/utils/date-utils';
import { SampleType } from '@/gql/graphql';
import SampleDetail from '@/components/sample/SampleDetail.vue';
import SampleForm from '@/components/sample/SampleForm.vue';
import SampleDelete from '@/components/sample/SampleDelete.vue';
import ProjectSampleAssociationDelete from '@/components/project/ProjectSampleAssociationDelete.vue';
import {
  BAlert,
  BButton,
  BButtonGroup,
  BCard,
  BTable,
} from 'bootstrap-vue-next';

// --- Extended Sample Inteface ---
export interface SampleTypeExtended extends SampleType {
  canEdit: boolean;
}

// --- Component properties ---
const props = defineProps<{
  samples: SampleTypeExtended[];
  loading: boolean;
  showBucket: boolean;
  showKit: boolean;
  showAssociatedProjects: boolean;
  assocProjectId: number | null; // If listing sample within a project
  assocProjectName: string | null; // If listing sample within a project
  canEditSampleAssoc: boolean; // If user can manage sample associations
}>();

const noSamples = computed(() => {
  return props.samples.length === 0;
});

// --- Component emits ---
const emits = defineEmits(['refresh']);

// --- Sample selection (details, edit, delete, create) ---
const selectedSample = ref<SampleType | null>(null);
const showModalSampleDetail = ref(false);
const showModalSampleEdit = ref(false);
const showModalSampleDelete = ref(false);
const showModalProjectSampleAssociationDelete = ref(false);

// --- Handle sample actions ---
function handleSampleDetailRequest(sample: SampleType) {
  console.debug('Sample details requested:', sample.name);
  selectedSample.value = sample;
  showModalSampleDetail.value = true;
}

function handleSampleEditRequest(sample: SampleType) {
  console.debug('Sample edit requested:', sample.name);
  selectedSample.value = sample;
  showModalSampleEdit.value = true;
}

function handleSampleDeleteRequest(sample: SampleType) {
  console.debug('Sample deletion requested:', sample.name);
  selectedSample.value = sample;
  showModalSampleDelete.value = true;
}

function handleSampleProjectAssociationDeleteRequest(sample: SampleType) {
  console.debug('Sample project association deletion requested:', sample.name);
  selectedSample.value = sample;
  showModalProjectSampleAssociationDelete.value = true;
}

function onSampleFormSuccess() {
  closeModals();
  emits('refresh');
}

function onSampleDeleteSuccess() {
  closeModals();
  emits('refresh');
}

function onProjectSampleAssociationDeleteSuccess() {
  closeModals();
  emits('refresh');
}

function closeModals() {
  selectedSample.value = null;

  showModalSampleDetail.value = false;
  showModalSampleEdit.value = false;
  showModalSampleDelete.value = false;
  showModalProjectSampleAssociationDelete.value = false;
}
// --- Sample Table Fields ---
const sampleTableFields = computed(() => {
  // First define fields that are always present
  const fields = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      tdClass: 'align-middle',
    },
    {
      key: 'description',
      label: 'Description',
      sortable: true,
      tdClass: 'align-middle',
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      formatter: formatDate,
      tdClass: 'align-middle',
    },
    { key: 'id', label: '', tdClass: 'align-middle text-end min-width' },
  ];
  // Kit field (optional)
  if (props.showKit) {
    fields.splice(2, 0, {
      key: 'kit',
      label: 'Kit',
      sortable: true,
      tdClass: 'align-middle',
    });
  }
  // Bucket field (optional)
  if (props.showBucket) {
    fields.splice(2, 0, {
      key: 'bucket.friendlyName',
      label: 'Data Bucket',
      sortable: true,
      tdClass: 'align-middle',
    });
  }
  return fields;
});
</script>
<template>
  <div>
    <b-alert v-model="noSamples" variant="secondary">No samples found.</b-alert>
    <BCard v-if="props.samples.length > 0" class="mb-3">
      <b-table
        :items="props.samples"
        :busy="loading"
        :fields="sampleTableFields"
        small
        striped
        responsive
        hover
        class="browser-table"
        @row-clicked="handleSampleDetailRequest"
      >
        <template #cell(name)="data">
          <strong>{{ data.value }}</strong>
        </template>
        <template #cell(kit)="data">
          {{ data.value.name }} ({{ data.value.code.code }})
        </template>
        <template #cell(id)="data">
          <BButtonGroup class="action-buttons">
            <BButton
              size="sm"
              variant="outline-secondary"
              @click="handleSampleDetailRequest(data.item)"
            >
              <font-awesome-icon icon="magnifying-glass" />
            </BButton>
            <template v-if="props.assocProjectId">
              <BButton
                size="sm"
                variant="outline-secondary"
                @click="handleSampleProjectAssociationDeleteRequest(data.item)"
                v-if="props.canEditSampleAssoc"
                :id="`btn-remove-sample-assoc-${data.item.id}`"
                v-b-tooltip.hover
                title="Remove sample from project"
              >
                <font-awesome-icon
                  :id="`icon-remove-project-sample-assoc-${data.item.id}`"
                  icon="xmark"
                />
              </BButton>
            </template>
            <template v-else>
              <BButton
                size="sm"
                variant="outline-secondary"
                @click="handleSampleEditRequest(data.item)"
                v-if="data.item.canEdit"
              >
                <font-awesome-icon icon="pencil" />
              </BButton>
              <BButton
                size="sm"
                variant="outline-secondary"
                @click="handleSampleDeleteRequest(data.item)"
                v-if="data.item.canEdit"
              >
                <font-awesome-icon icon="trash" />
              </BButton>
            </template>
          </BButtonGroup>
        </template>
      </b-table>
    </BCard>

    <!-- MODAL: Sample Details -->
    <div v-if="selectedSample">
      <SampleDetail
        :sample="selectedSample"
        :show="showModalSampleDetail"
        @close="closeModals"
      />
    </div>

    <!-- MODAL: Sample Edit -->
    <div
      v-if="
        selectedSample &&
        selectedSample.id &&
        selectedSample.name &&
        selectedSample.fileR1 &&
        selectedSample.fileR2
      "
    >
      <SampleForm
        :bucket-id="null"
        :sample-id="selectedSample.id"
        :sample-name="selectedSample.name"
        :sample-description="selectedSample.description"
        :sample-file-r1="selectedSample.fileR1"
        :sample-file-r2="selectedSample.fileR2"
        :kit-code="selectedSample.kit?.code?.code || ''"
        :warning="null"
        :show="showModalSampleEdit"
        @close="closeModals"
        @close-and-refresh="onSampleFormSuccess"
      />
    </div>

    <!-- MODAL: Project-Sample Association Delete -->
    <div
      v-if="
        selectedSample &&
        selectedSample.name &&
        selectedSample.id &&
        props.assocProjectId &&
        props.assocProjectName &&
        props.canEditSampleAssoc
      "
    >
      <ProjectSampleAssociationDelete
        :sample-name="selectedSample.name"
        :sample-id="selectedSample.id"
        :projectId="props.assocProjectId"
        :projectName="props.assocProjectName"
        :show="showModalProjectSampleAssociationDelete"
        @close="closeModals"
        @close-and-refresh="onProjectSampleAssociationDeleteSuccess"
      />
    </div>

    <!-- MODAL: Sample Delete -->
    <div v-if="selectedSample">
      <SampleDelete
        :sample-name="selectedSample.name || ''"
        :sample-id="selectedSample.id || 0"
        :show="showModalSampleDelete"
        @close="closeModals"
        @close-and-refresh="onSampleDeleteSuccess"
      />
    </div>
  </div>
</template>
<style scoped>
.browser-table {
  font-size: 0.8rem;
}
:deep(.min-width) {
  width: 1px !important;
  white-space: nowrap;
}
</style>
